import React, { useState, useEffect, useRef } from 'react'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import AdxDropdownWithFilter from 'components/reusable/AdxDropdownWithFilter'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import defaultAvatar from 'assets/images/default_avatar.png'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import { getAllStaffEmployees } from 'services/api/modules/staff-management/staffManagementDashboard'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import {
  getDepartmentList,
  getDepartmentListById,
  postDepartmentList,
  putDepartmentList,
  deleteDepartmentList
} from 'services/api/department-configuration'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxButton from 'components/custom/AdxButton'
import DeleteDepartmentModal from 'components/reusable/modal/DeleteDepartmentModal'


const AddDepartmentModal = ({
  showAddDepartmentModal,
  handleCloseAddDepartmentModal,
  setStructureId,
  structureId,
  setSelectedCardId,
  selectedCardId,
}) => {
  const [selectValue, setSelectValue] = useState('')
  const [selectValueDepartment, setSelectValueDeparment] = useState('')
  const [dropdownFilterOptions, setDropdownFilterOptions] = useState()
  const [dropDownDepartmentList, setDropDownDepartmentList] = useState([])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('')


  const formikRef = useRef()
  const queryClient = useQueryClient()

  const [initialValues, setInitialValues] = useState({
    name: '',
    head: '',
    parent: '',
    structure_id: structureId,
  })

  const isEdit = Boolean(selectedCardId)
  const isProtected = initialValues?.name === 'Head of Organization' || selectedCardId === 1

  const handleSetSelectValue = (event) => {
    setSelectValue(event.target.value)
  }

  const handleSetSelectValueDepartment = (event) => {
    setSelectValueDeparment(event.target.value)
  }

  const allStaffEmployees = useQuery('allStaffEmployees', async () => {
    const { data } = await getAllStaffEmployees()
    const flatData = Object.values(data.staff).flat()
    return flatData
  })

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Department name is required'),
    head: Yup.number().required('Head of department is required'),
    parent: Yup.number().required('Sub-department of is required'),
  })

  const departmentList1 = useQuery('departmentList1', async () => {
    const { data } = await getDepartmentList()
    const newData = data?.map((item) => item.departments).flat()
    return newData
  })

  const handleCloseModal = () => {
    setStructureId(null)
    setSelectedCardId(null)
    handleCloseAddDepartmentModal()
    setSelectValue('')
    setSelectValueDeparment('')
    // formikRef.current.resetForm()
  }

  const openDeleteModal = () => {
    setDeleteErrorMessage('')
    setIsDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setDeleteErrorMessage('')
  }

  const handleConfirmDelete = async () => {
    if (!selectedCardId) return
    setDeleteErrorMessage('')

    try {
      await deleteMutation.mutateAsync()
      closeDeleteModal()
    } catch (e) {
      // "Gym Retail Operations still has child departments. Unable to delete"
      const msg = e?.data?.message || 'Unable to delete department'
      setDeleteErrorMessage(msg)

    }
  }


  const getInitialValues = async () => {
    try {
      const res = await getDepartmentListById(selectedCardId)
      const dept = res?.data?.department

      const headId = dept?.head?.id ?? ''
      const parentId = dept?.parent_id ?? ''

      setInitialValues({
        name: dept?.name ?? '',
        head: headId,
        parent: parentId,
        structure_id: dept?.structure?.id ?? structureId,
      })

      setSelectValue(
        headId ? (allStaffEmployees?.data?.find((item) => item?.id === headId) || '') : ''
      )


      setSelectValueDeparment(
        parentId ? (departmentList1?.data?.find((item) => item?.id === parentId) || '') : ''
      )

      // ✅ safe filter list
      const prevLayerId = (dept?.structure?.id ?? 0) - 1
      setDropDownDepartmentList(
        (departmentList1?.data ?? []).filter((item) => item?.structure?.id === prevLayerId)
      )
    } catch (e) { }
  }


  useEffect(() => {
    if (selectedCardId === null) {
      setInitialValues({
        name: '',
        head: '',
        parent: '',
        structure_id: structureId,
      })
      setDropDownDepartmentList(
        departmentList1?.data?.filter((item) => item.structure.id === structureId - 1)
      )
    } else {
      getInitialValues()
    }
  }, [structureId, selectedCardId])

  const mutation = useMutation(
    async (values) => {
      if (selectedCardId) {
        await putDepartmentList(selectedCardId, values)
      } else {
        await postDepartmentList(values)
      }
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('departmentList')
        await queryClient.invalidateQueries('departmentList1')
        handleCloseModal()
      },
      onError: (e) => {
        if (e.status === 422) {
          handleValidationErrors(e, formikRef)
        } else {
          console.log(e.data.message)
        }
      },
    }
  )

  const deleteMutation = useMutation(async () => {
    return await deleteDepartmentList(selectedCardId)
  }, {
    onSuccess: async () => {
      await queryClient.invalidateQueries('departmentList')
      await queryClient.invalidateQueries('departmentList1')
      handleCloseModal()
    },
  })


  const handleDelete = async () => {
    if (!selectedCardId) return

    const confirmed = window.confirm('Delete this department?')
    if (!confirmed) return

    await deleteMutation.mutateAsync()
  }


  const handleOnSubmit = async (values) => {
    await mutation.mutate(values)
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => handleOnSubmit(values, actions)}
      innerRef={formikRef}
    >
      {({ errors, touched, values, setFieldValue, handleChange, handleBlur, handleSubmit }) => (
        <Form>


          <AdxFormModal
            title="Add department"
            subtitle=""
            open={showAddDepartmentModal}
            onClose={handleCloseModal}
            handleClose={handleCloseModal}
            sxModal={{ minWidth: { xs: 1, md: 700 } }}
            enableFooter
            footerprops={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column-reverse', md: 'row' },
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'end' },
                  gap: 1,
                  px: 3,
                  py: 2,
                  zIndex: 100,
                  bgcolor: 'primary.light',
                  borderColor: 'grayscale.line-light',
                }}
              >

                <Box sx={{ width: { xs: 1, md: 'fit-content' } }}>
                  {isEdit && !isProtected && (
                    <AdxButton
                      variant="outlined"
                      sx={{ minWidth: '160px', width: { xs: 1, md: 'fit-content' } }}
                      onClick={openDeleteModal}
                      disabled={deleteMutation.isLoading}
                    >
                      Delete
                    </AdxButton>
                  )}

                  <AdxButton
                    sx={{ minWidth: '200px', width: { xs: 1, md: 'fit-content' } }}
                    onClick={handleSubmit}
                    disabled={mutation.isLoading || deleteMutation.isLoading}
                  >
                    {isEdit ? 'Save' : 'Add'}
                  </AdxButton>
                </Box>



              </Box>
            }







          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography color="light-gray-text.main">
                Add department to this layer by filling in the details below
              </Typography>
              {/* <pre>{JSON.stringify(values, null, 4)}</pre> */}
              <Field
                as={AdxInput}
                name="name"
                label="Department name"
                size="standard"
                placeholder="E.g : UIUX"
                charLimit={30}
                sx={{ width: { xs: '100%', lg: '100%' } }}
                onChange={handleChange}
                onBlur={handleBlur}
                isErrorShown={errors.name && touched.name}
                errorMessage={<ErrorMessage name="name" component={AdxErrorMessage} />}
              />
              <Field
                as={AdxDropdown}
                label="Sub-department of"
                name="parent"
                sx={{ width: '100%' }}
                value={selectValueDepartment}
                onChange={(e) => {
                  handleSetSelectValueDepartment(e)
                  setFieldValue('parent', e.target.value.id)
                }}
                isErrorShown={errors.parent && touched.parent}
                errorMessage={<ErrorMessage name="parent" component={AdxErrorMessage} />}
              >
                {dropDownDepartmentList?.map((item) => (
                  <AdxMenuItem value={item} selectedvalue={selectValueDepartment}>
                    {item?.name}
                  </AdxMenuItem>
                ))}
              </Field>

              <Field
                as={AdxDropdownWithFilter}
                name="head"
                label="Head of department"
                allOptions={allStaffEmployees?.data}
                setOptions={setDropdownFilterOptions}
                filters={['name']}
                sx={{ width: '100%' }}
                value={selectValue}
                isErrorShown={errors.head && touched.head}
                errorMessage={<ErrorMessage name="head" component={AdxErrorMessage} />}
                onChange={(e) => {
                  handleSetSelectValue(e)
                  setFieldValue('head', e.target.value.id)
                }}
                renderValue={(option) => {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: 'gray-background.main',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          mx: 1,
                        }}
                      >
                        <Avatar src={option?.profile_image_url || defaultAvatar} />
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography variant="body1">{option?.name}</Typography>
                        <Typography variant="caption">{option?.role}</Typography>
                      </Box>
                    </Box>
                  )
                }}
              >
                {dropdownFilterOptions?.map((item, index) => (
                  <AdxMenuItem value={item} selectedvalue={selectValue} key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: 'gray-background.main',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                        }}
                      >
                        <Avatar src={item?.profile_image_url || defaultAvatar} />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography variant="body1">{item?.name}</Typography>
                      </Box>
                    </Box>
                  </AdxMenuItem>
                ))}
              </Field>
            </Box>
          </AdxFormModal>

          <DeleteDepartmentModal
            sx={{ minWidth: '100px', width: { xs: 1, md: 'fit-content' } }}
            open={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onConfirm={handleConfirmDelete}
            isLoading={deleteMutation.isLoading}
            errorMessage={deleteErrorMessage}
            title="Delete this department?"
            subtitle="This will remove the department from the list."
          />

        </Form>
      )}
    </Formik>
  )
}

export default AddDepartmentModal
